import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form } from 'formik';
import FormInput from '../Generals/FormInput/FormInput';
import { LoginPasswordAction, LoginLinkGroupWrapper } from './Login.style';
import LoginCard from './LoginCard';
import {
  createInitialValues,
  createValidationSchema,
  renderErrorList,
  renderLoginButton,
} from './loginFormUtils';

const CLIENT_ID = process.env.CLIENT_ID;
const CLASSLINK_CLIENT_ID = process.env.CLASSLINK_CLIENT_ID;
const REDIRECT_URI = `${window.location.origin}/auth/complete/clever/`;
const CLEVER_LOGIN_URL = `https://clever.com/oauth/authorize?response_type=code&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}`;
const CLASSLINK_REDIRECT_URI = `${window.location.origin}/auth/complete/classlink/`;
const CLASSLINK_LOGIN_URL = `https://launchpad.classlink.com/oauth2/v2/auth?scope=profile&redirect_uri=${CLASSLINK_REDIRECT_URI}&client_id=${CLASSLINK_CLIENT_ID}&response_type=code`;

interface LoginFormValues {
  email: string;
  password?: string;
}

interface StudentLoginFormProps {
  isLoading?: boolean;
  errorMessage?: string | string[] | null;
  noTypeLogin: (email: string, password?: string) => void;
  emailOnlyProviderLogin?: (email: string) => void;
  emailOnlyLogin?: boolean;
}

const renderPasswordAction = (): JSX.Element => (
  <LoginPasswordAction>
    <label>
      <input type="checkbox" />
      <span className="checkmark" />
      <span>Remember me</span>
    </label>
    <Link to="/forgot-password">Forgot your password ?</Link>
  </LoginPasswordAction>
);

const renderCleverLinkGroup = (): JSX.Element => (
  <LoginLinkGroupWrapper is_clever>
    <a href={CLEVER_LOGIN_URL}>
      <span className="sr-only">Log in with Clever</span>
    </a>
  </LoginLinkGroupWrapper>
);

const renderClassLinkLinkGroup = (): JSX.Element => (
  <LoginLinkGroupWrapper is_clever={false}>
    <a href={CLASSLINK_LOGIN_URL}>
      <span className="sr-only">Log in with ClassLink</span>
    </a>
  </LoginLinkGroupWrapper>
);

const StudentLoginForm: React.FC<StudentLoginFormProps> = ({
  isLoading = false,
  errorMessage,
  noTypeLogin,
  emailOnlyProviderLogin,
  emailOnlyLogin = false,
}) => {
  const handleSubmit = useCallback(
    (values: LoginFormValues) => {
      const { email, password } = values;
      if (emailOnlyLogin && emailOnlyProviderLogin) {
        emailOnlyProviderLogin(email);
      } else if (emailOnlyLogin) {
        noTypeLogin(email);
      } else {
        noTypeLogin(email, password);
      }
    },
    [noTypeLogin, emailOnlyProviderLogin, emailOnlyLogin],
  );

  return (
    <LoginCard
      isLoading={isLoading}
      footerContent={
        !emailOnlyLogin ? (
          <>
            {renderCleverLinkGroup()}
            {renderClassLinkLinkGroup()}
          </>
        ) : null
      }
    >
      <Formik<LoginFormValues>
        initialValues={createInitialValues({ includePassword: !emailOnlyLogin })}
        validationSchema={createValidationSchema({ requirePassword: !emailOnlyLogin })}
        onSubmit={handleSubmit}
      >
        <Form noValidate>
          <FormInput
            style={{ fontWeight: 'bold' }}
            type="email"
            name="email"
            id="email-input"
            label="Email"
            aria-label="Email address"
            autoComplete="email"
          />
          {!emailOnlyLogin && (
            <>
              <FormInput
                type="password"
                name="password"
                label="Password"
                aria-label="Password"
                autoComplete="current-password"
              />
              {renderPasswordAction()}
            </>
          )}
          {renderErrorList(errorMessage)}
          {renderLoginButton(isLoading)}
        </Form>
      </Formik>
    </LoginCard>
  );
};

export default StudentLoginForm;

